import { RegistrationGroup } from "../../api/getEventTransactionDetails/types";
import { format } from "date-fns";
import { ChevronRight } from "lucide-react";

interface RegistrationCardProps {
  group: RegistrationGroup;
  onClick: () => void;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(dateString: string) {
  try {
    return format(new Date(dateString), "MMM d, yyyy");
  } catch {
    return dateString;
  }
}

function getStatusInfo(group: RegistrationGroup) {
  if (group.hasPendingRefunds) {
    return { label: "Pending Refund", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" };
  }
  if (group.refundedAmount > 0 && group.remainingRefundableAmount <= 0) {
    return { label: "Fully Refunded", className: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300" };
  }
  if (group.refundedAmount > 0) {
    return { label: "Partially Refunded", className: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300" };
  }
  return { label: "Paid", className: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300" };
}

export function RegistrationCard({ group, onClick }: RegistrationCardProps) {
  const status = getStatusInfo(group);
  const invoiceNumbers = group.invoices.map((i) => i.invoiceNumber).join(", ");

  const summaryParts: string[] = [];
  summaryParts.push(
    `${group.actualAttendeeCount} attendee${group.actualAttendeeCount !== 1 ? "s" : ""}`,
  );
  if (group.voucherCount > 0) {
    summaryParts.push(
      `${group.voucherCount} voucher${group.voucherCount !== 1 ? "s" : ""}`,
    );
  }
  if (invoiceNumbers) {
    summaryParts.push(`#${invoiceNumbers}`);
  }

  return (
    <button
      className="w-full text-left rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-750 transition-colors group"
      onClick={onClick}
      type="button"
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-0.5">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {group.companyName}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
              {formatDate(group.createdAt)}
            </span>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
            {summaryParts.join(" · ")}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {formatCurrency(group.totalPaid)}
            </div>
          </div>
          <span
            className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${status.className}`}
          >
            {status.label}
          </span>
          <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 transition-colors" />
        </div>
      </div>
    </button>
  );
}
